import fetch from 'isomorphic-fetch';
import fetchJsonp from 'fetch-jsonp';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import AuthHelper from './AuthHelper';
import LoggerHelper from '../logs/LoggerHelper';
import { getCookie } from '../helpers/Cookies';
import LocalStorage from '../helpers/LocalStorage';
import { cstToUTC } from '../Common/CommonHelper';
import { slowApiTimeThreshold, ssrApiDefaultTimeout, apiHosts } from './FetchHelper.config';

export const REQUEST_METHODS = {
    GET: 'GET',
    POST: 'POST',
    PUT: 'PUT',
    DELETE: 'DELETE',
    PATCH: 'PATCH',
};

const CREDENTIALS = {
    OMIT: 'omit',
    SAME_ORIGIN: 'same-origin',
    INCLUDE: 'include',
};

const FUSION_HEADERS = [
    'x-store-number',
    'x-associate-number',
    'x-terminal-number',
    'x-pos-authtoken',
    'x-device-type-dept',
    'x-zipcode',
];

const credentialsEnumToString = (credentialsEnum) => {
    switch (credentialsEnum) {
        case CREDENTIALS.INCLUDE:
            return 'include';
        case CREDENTIALS.OMIT:
            return 'omit';
        case CREDENTIALS.SAME_ORIGIN:
            return 'same-origin';
        default:
            return 'same-origin';
    }
};

export const getXCloudHeader = (host) => {
    switch (host) {
        case 'm-dt-test1.jcpenney.com':
        case 'dt-test1.jcpenney.com':
            return 'aws_integration1';
        case 'posf-cloud-test4.jcpenney.com':
        case 'm-dt-test2.jcpenney.com':
        case 'dt-test2.jcpenney.com':
            return 'aws_integration2';
        case 'posf-cloud-test3.jcpenney.com':
        case 'm-dt-test3.jcpenney.com':
        case 'dt-test3.jcpenney.com':
            return 'aws_integration3';
        case 'm-dt-test4.jcpenney.com':
        case 'dt-test4.jcpenney.com':
            return 'aws_integration4';
        case 'm-dt-loadtest2.jcpenney.com':
        case 'dt-loadtest2.jcpenney.com':
        case 'm-dt-loadtest.jcpenney.com':
        case 'dt-loadtest.jcpenney.com':
        case 'posf-cloud-loadtest.jcpenney.com':
            return 'aws_loadtest2';
        case 'dt-loadtest1.jcpenney.com':
        case 'posf-cloud-loadtest1.jcpenney.com':
            return 'aws_loadtest1';
        case 'posf-cloud-test5.jcpenney.com':
        case 'm-dt-test5.jcpenney.com':
        case 'dt-test5.jcpenney.com':
            return 'aws_integration5';
        default:
            return null;
    }
};

const getFormatedCookie = (cookie) => {
    let formatedCookie = '';
    forEach(cookie, (value, key) => {
        formatedCookie += `${key}=${value};`;
    });
    return formatedCookie;
};

const getFormatedCookieForSSR = (cookie) => {
    let formatedCookie = '';
    forEach(cookie, (value, key) => {
        const encodeValue = encodeURIComponent(value);
        formatedCookie += `${key}=${encodeValue};`;
    });
    return formatedCookie;
};

/* istanbul ignore next */
const getFusionHeaders = () => {
    const headerValue = [];
    forEach(FUSION_HEADERS, (header) => {
        const val = LocalStorage.getData(header);
        headerValue.push(val);
    });
    return headerValue.join('|');
};

export const getDeviceType = (context) => {
    let deviceType = 'desktop';
    try {
        if (context.deviceType.isMobile) {
            deviceType = 'mobile';
        } else if (context.deviceType.isTablet) {
            deviceType = 'tablet';
        }
    } catch (e) {
        deviceType = '';
    }

    return deviceType;
};

export const formatDateForPreview = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    let hours = date.getHours();
    let minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours %= 12;
    hours = hours || 12; // the hour '0' should be '12'
    minutes = minutes < 10 ? `0${minutes}` : minutes;

    return `${year}.${month}.${day} ${hours}:${minutes} ${ampm}`;
};

export const getApiParams = (requestMethod, payload, reqHeaders, config, context, url) => {
    const params = {
        method: requestMethod,
        headers: {},
        /**
         * credentials (String) - Authentication credentials mode. Default: "omit"
         * "omit" - don't include authentication credentials (e.g. cookies) in the request
         * "same-origin" - include credentials in requests to the same site
         * "include" - include credentials in requests to all sites
         */
        credentials: credentialsEnumToString(config.credentials),
    };
    const isNative = get(context, 'isNative', false);
    const cloudVersion = get(context, 'cloudVersion', '');
    const featureFlags = (context && context.featureFlags) || {};

    if (__SERVER__ && config.requestHeaderCookie && reqHeaders) {
        params.headers = reqHeaders;
        delete params.headers.host;
    }

    if (config && config.referrerPolicy) {
        params.referrerPolicy = config.referrerPolicy;
    }

    if (requestMethod !== REQUEST_METHODS.GET && payload) {
        params.body = config.isFormData ? payload : JSON.stringify(payload);
    }

    if (reqHeaders && reqHeaders.cookie) {
        params.headers.cookie = reqHeaders.cookie;
    }

    if (__SERVER__ && config.enableRequestHeaderCookie && reqHeaders && reqHeaders.cookies) {
        params.headers.cookie = getFormatedCookieForSSR(reqHeaders.cookies);
    }

    if (config && config.isAuthRequired && config.accessToken) {
        params.headers.Authorization = `Bearer ${config.accessToken}`;
    }

    if (config && config.isSourceTokenRequired) {
        const headerName = 'X-SOURCE-TOKEN';
        const authToken = AuthHelper.getAuthToken();
        if (config.isCreateAccountPostOrder && config.accessToken) {
            params.headers[headerName] = config.accessToken;
        } else if (authToken) {
            params.headers[headerName] = authToken;
        }
    }
    /* istanbul ignore if */
    if (config && config.isDPClusterRequired) {
        if (getCookie('DPCluster')) {
            params.headers.DPCluster = getCookie('DPCluster');
        }
        if (getCookie('XSessionId')) {
            params.headers.DPJSESSIONID = getCookie('XSessionId');
        }
        if (getCookie('DPInstance')) {
            params.headers.DPInstance = getCookie('DPInstance');
        }
    }

    if (config && config.contentType) {
        params.headers['content-type'] = `${config.contentType};charset=utf-8`;
    }

    // Fusion specific headers
    if (featureFlags.enableFusionTracking && !config.external) {
        const headerName = 'X-FUSION-TRACKING';
        params.headers[headerName] = getFusionHeaders();
    }

    if (featureFlags.enableCloudOrigin && !config.external) {
        const headerName = 'x-jcpcloud-origin';
        params.headers[headerName] = 'beta';
    }

    if (config && config.isGuestUser) {
        const headerName = 'X-GUEST-USER';
        params.headers[headerName] = 'true';
    }

    if (config && config.isXCloudHeaderRequired && config.hostName !== 'www.jcpenney.com') {
        const headerName = 'X-JCPCLOUD-ENV';
        params.headers[headerName] = getXCloudHeader(config.hostName);
    }

    if (config && config.accept) {
        params.headers.Accept = config.accept;
    }

    if (config && config.xCommand) {
        const headerName = 'X-COMMAND';
        params.headers[headerName] = config.xCommand;
    }

    if (config && config.xStrategy) {
        const headerName = 'X-STRATEGY';
        params.headers[headerName] = config.xStrategy;
    }

    if (config && config.channel) {
        const headerName = 'X-CHANNEL';
        params.headers[headerName] = config.channel;
    }

    if (config && config.interstitial) {
        const headerName = 'X-Cart-Interstitial';
        params.headers[headerName] = config.interstitial;
    }

    if (config && config.isXStandardizationRequired) {
        const headerName = 'X-STANDARDIZATION-REQUIRED';
        params.headers[headerName] = config.isXStandardizationRequired;
    }

    if (config && config.removeRestrictedItems) {
        const headerName = 'x-delete-restricted-items';
        params.headers[headerName] = config.removeRestrictedItems;
    }

    if (config && config.isRewardsAuthLink) {
        const headerName = 'X-AUTH-FLOW';
        params.headers[headerName] = config.isRewardsAuthLink;
    }

    if (config && config.isMigrationBucket) {
        const headerName = 'X-MIGRATION-BUCKET';
        params.headers[headerName] = config.isMigrationBucket;
    }

    if (config && config.userAction) {
        const headerName = 'X-USER-ACTION';
        params.headers[headerName] = config.userAction;
    }

    if (config && config.isDeleteAll) {
        const headerName = 'X-DELETE-ALL';
        params.headers[headerName] = config.isDeleteAll;
    }

    if (config && config.listing) {
        params.headers.excludes = 'DELIVERY_DATES';
        params.headers['X-CLIENT-SOURCE'] = 'CAM';
    }

    if (config && config.XShareAccountId) {
        params.headers['X-SHARE-SRC-ACCID'] = config.XShareAccountId;
    }

    if (config && config.XShareToken) {
        params.headers['X-SHARE-TOKEN'] = config.XShareToken;
    }

    // impulse headers
    if (config && config.action) {
        const headerName = 'X-ACTION';
        params.headers[headerName] = config.action;
    }
    if (config && config.xMode) {
        const headerName = 'X-MODE';
        params.headers[headerName] = config.xMode;
    }

    if (config && config.xClientOrigin) {
        params.headers['X-CLIENT-ORIGIN'] = config.xClientOrigin;
    }

    if (config && config.xGetLines) {
        params.headers['X-GET-LINES'] = config.xGetLines;
    }

    if (config && config.xGetTracking) {
        params.headers['X-GET-TRACKING'] = config.xGetTracking;
    }

    if (config && config.isResetAuthRequired && config.accessToken) {
        params.headers.Authorization = `Reset ${config.accessToken}`;
    }

    if (config && config.orderId) {
        const headerName = 'X-ORDER-ID';
        params.headers[headerName] = config.orderId;
    }

    if (config && config.orderSource) {
        const headerName = 'X-ORDER-SOURCE';
        params.headers[headerName] = config.orderSource;
    }

    if (config && config.recaptchaToken) {
        const headerName = 'X-CAPTCHA-TOKEN';
        params.headers[headerName] = config.recaptchaToken;
    }

    if (config && config.acceptVersion) {
        const headerName = 'Accept-Version';
        params.headers[headerName] = config.acceptVersion;
    }

    if (config && config.isClientIdentifier) {
        const isClientIdentifier = 'isClientIdentifier';
        params.headers[isClientIdentifier] = config.isClientIdentifier;
    }

    if (config && config.isReferenceValidHeader && config.isReferenceValid) {
        params.headers['X-Reference-Valid'] = config.isReferenceValid;
    }

    if (config && config.XForwardedChannelHeader) {
        params.headers['X-JCP-Forwarded-Proto'] = 'https';
        params.headers['X-JCP-Forwarded-Host'] = config.hostname
            ? config.hostname
            : 'www.jcpenney.com';
        params.headers['X-JCP-Forwarded-Channel'] = config.XForwardedChannelHeader;
        if (config.version) {
            params.headers['Accept-version'] = config.version;
        }
    }

    if (config && config.XForwardedChannelHeaderHost) {
        params.headers['X-JCP-Forwarded-Proto'] = 'https';
        params.headers['X-JCP-Forwarded-Host'] = config.host ? config.host : 'www.jcpenney.com';
        params.headers['X-JCP-Forwarded-Channel'] = config.XForwardedChannelHeaderHost;
    }

    if (config && config.xClientId) {
        params.headers['X-Client-Id'] = `Basic ${config.xClientId}`;
    }

    if (config && config.xState) {
        params.headers['X-STATE'] = config.xState;
    }

    if (config && config.xZip) {
        params.headers['X-ZIPCODE'] = config.xZip;
    }

    if (config && config.xCountry) {
        params.headers['X-COUNTRY'] = config.xCountry;
    }

    if (config && config.guestAccountId) {
        params.headers['X-GUEST-ACCOUNT-ID'] = config.guestAccountId;
    }

    if (config && config.isAutoLoginReq) {
        params.headers['X-AUTO-LOGIN'] = config.isAutoLoginReq;
    }

    if (config && config.requestHeaderCookie) {
        params.headers.cookie = getFormatedCookie(config.requestHeaderCookie);
        params.headers['x-yoda-origin'] = 'yoda-node';
        if (config.geoDataHeader) {
            params.headers['X-GEODATA'] = config.geoDataHeader;
        }
    }

    // captcha implamentation for giftcard balance
    if (config && config.captchaToken && config.captchaType) {
        params.headers['g-recaptcha-response'] = config.captchaToken;
        params.headers['x-captcha-type'] = config.captchaType;
    }

    // timeout is only supported by server side with node-fetch
    if (__SERVER__) {
        if (context.httpAgent && context.httpsAgent && url) {
            if (url.startsWith('https')) {
                params.agent = context.httpsAgent;
            } else if (url.startsWith('http')) {
                params.agent = context.httpAgent;
            }
        }

        if (config && !isNaN(config.timeout) && config.timeout > 0) {
            params.timeout = config.ssrDefaultTimeOut ? config.ssrDefaultTimeOut : config.timeout;
        } else {
            params.timeout = config.ssrDefaultTimeOut
                ? config.ssrDefaultTimeOut
                : ssrApiDefaultTimeout;
        }

        if (context) {
            const deviceType = getDeviceType(context);
            params.headers['User-Agent'] = `node-fetch/1.0 (${deviceType})`;
        }
    }
    if (config) {
        if (config.previewDate) {
            params.headers['X-Content-Date'] = config.previewDate;
        } else if (
            context &&
            context.isPreview &&
            [apiHosts.appHost, apiHosts.browse, apiHosts.search].includes(config.apiHost)
        ) {
            params.headers.previewDate = formatDateForPreview(
                new Date(getCookie('DP_PREVIEW_DATE') || new Date())
            );
        } else if (context && context.isPreview && apiHosts.content === config.apiHost) {
            const dateValue = cstToUTC(getCookie('DP_PREVIEW_DATE'), context);
            // Check for the Preview Environment Date
            params.headers['X-Content-Date'] = (dateValue || new Date()).toISOString();
        }
    }

    if (config && config.xApplePayInitiateCheckout) {
        if (config.xState) {
            params.headers['X-STATE'] = config.xState;
        }
        if (config.xZip) {
            params.headers['X-ZIPCODE'] = config.xZip;
        }
    }

    if (config && config.isDPOrder && getCookie('DPOrder')) {
        params.headers['X-DPOrder'] = getCookie('DPOrder');
    }

    if (config && config.guestSavedItems && config.savedItems) {
        params.headers['view-myfavorites'] = config.savedItems;
    }

    if (config && config.dataType) {
        params.headers['X-DATA-TYPE'] = config.dataType;
    }

    if (config && config.emailOptIn) {
        params.headers['X-EMAIL-OPT-IN'] = config.emailOptIn;
    }

    if (config && config.isXOriginHeaderRequired && config.xOriginValue) {
        params.headers['X-Origin'] = config.xOriginValue;
    }

    if (config && config.xAllAvailableItems) {
        params.headers['x-all-available-items'] = config.xAllAvailableItems;
    }

    if (config && config.xEtagValue) {
        params.headers['X-ETag'] = config.xEtagValue;
    }

    if (config && config.oldAccountId) {
        params.headers['X-GUEST-ID'] = config.oldAccountId;
    }

    if (__SERVER__ && reqHeaders && reqHeaders['x-jcpcloud-origin']) {
        params.headers['x-jcpcloud-origin'] = reqHeaders['x-jcpcloud-origin'];
    }

    if (isNative && cloudVersion) {
        params.headers['x-jcpcloud-origin'] = cloudVersion;
    }

    if (config && config.isXSourceAccountId) {
        params.headers['X-Src-Acc-Id'] = config.isXSourceAccountId;
    }

    if (config && config.xDeleteOOSItems) {
        params.headers['x-delete-oos-items'] = config.xDeleteOOSItems;
    }

    if (config && config.xClientSource) {
        params.headers['x-client-source'] = config.xClientSource;
    }

    if (config && config.excludes) {
        params.headers.excludes = config.excludes;
    }

    /* eslint dot-notation: ["error", { "allowPattern": "^[a-z]+(_[a-z]+)+$" }] */
    if (config && config.jcpHeader) {
        params.headers['jcp_version'] = 'GREEN';
    }

    // if config has custom headers object, then set those into headers. This way we can eliminate the sequence if conditions
    if (config && config.customHeaders) {
        params.headers = { ...params.headers, ...config.customHeaders };
    }

    // TODO: to clean up as impulse BE confirms the fix
    if (config && (config.isAuthUpdatePayment || config.isGuestUpdatePayment)) {
        const headerName = 'X-ACCOUNT-ID';
        params.headers[headerName] = config.accountId;
    }
    if (config && config.xAccountId) {
        const headerName = 'X-ACCOUNT-ID';
        params.headers[headerName] = config.xAccountId;
    }
    // TODO: to clean up as impulse BE confirms the fix
    if (config && config.isGuestUpdatePayment) {
        const headerName = 'X-PHONE-NO';
        params.headers[headerName] = config.phoneNumber;
    }
    if (config && config.xPhoneNumber) {
        const headerName = 'X-PHONE-NO';
        params.headers[headerName] = config.xPhoneNumber;
    }

    if (config && (config.isAuthUpdatePayment || config.isGuestUpdatePayment)) {
        params.headers['X-CLIENT-ORIGIN'] = 'CAM';
        params.headers['X-ACTION'] = 'PAYMENT-UPDATE';
    }

    if (config && config['x-optin-origin']) {
        params.headers['x-optin-origin'] = config['x-optin-origin'];
    }

    if (config?.isCMHeaderRequired) {
        params.headers['x-content-source'] = 'CM';
    }
    if (config?.cmPreviewDate) {
        params.headers['X-Preview-Date'] = config?.cmPreviewDate;
    }

    return params;
};

const logApiTiming = (url, timeToFinish) => {
    if (__SERVER__) {
        if (timeToFinish > slowApiTimeThreshold) {
            const logger = LoggerHelper.getLogger();
            logger.warn({ message: 'Slow Api', apiUrl: url, timeToFinish });
        }
    }
};

/**
 *This class is responsible for invoking API endpoints using fetch of isomorphic-fetch library.
 */

export default class RequestAPI {
    static CREDENTIALS = CREDENTIALS;

    /**
     * This function makes the call to back end API using below parameters and returns the promise back to the invoking client.
     *
     * @url - API end point URL.
     * @requestMethod - Request Method to call API  Ex: GET, POST, PUT, PATCH, DELETE.
     * @payload - Request payload to be send to API, this is an optional parameter and can be used for any POST,PUT and PATCH calls if required.
     */

    static invoke(url, requestMethod, payload, reqHeaders, config, context = {}) {
        const logger = LoggerHelper.getLogger();
        const startTime = Date.now();
        if (process.env.NODE_ENV === 'development') {
            url =
                url.indexOf('?') !== -1 ? `${url}&dpAkamaiOverride=1` : `${url}?dpAkamaiOverride=1`;
        }

        if (Array.isArray(context.apiCalled)) {
            const sliceTo = get(context, 'preferences.apiCalled.sliceTo', 300);
            context.apiCalled.push(url.length > sliceTo ? `${url.slice(0, sliceTo)}...` : url);
        }

        return fetch(url, getApiParams(requestMethod, payload, reqHeaders, config, context, url))
            .then((response) => {
                const { status } = response;
                const { statusText } = response;
                return response.text().then((text) => {
                    logApiTiming(url, Date.now() - startTime);
                    let parsedData;
                    try {
                        // Assuming all responses would have body content to parse as JSON
                        /**
                             * 200 OK :
                             * The request has succeeded. The information returned with the response is dependent on the method used in the request, for example:
                             GET an entity corresponding to the requested resource is sent in the response;
                             HEAD the entity-header fields corresponding to the requested resource are sent in the response without any message-body;
                             POST an entity describing or containing the result of the action;
                             TRACE an entity containing the request message as received by the end server.
                             */
                        parsedData = JSON.parse(text);

                        if (!(status > 199 && status < 300)) {
                            const err = LoggerHelper.createError(
                                `API failed: [Non-200 response] Body Content [${text}]`,
                                url,
                                { error: 'Non-200 response' },
                                status
                            );
                            logger.error({ err });
                        }
                    } catch (error) {
                        /**
                             * https://www.w3.org/Protocols/rfc2616/rfc2616-sec10.html
                             *
                             * 201 Created
                             The request has been fulfilled and resulted in a new resource being created. The newly created resource can be referenced by the URI(s) returned in the entity of the response, with the most specific URI for the resource given by a Location header field. The response SHOULD include an entity containing a list of resource characteristics and location(s) from which the user or user agent can choose the one most appropriate. The entity format is specified by the media type given in the Content-Type header field. The origin server MUST create the resource before returning the 201 status code. If the action cannot be carried out immediately, the server SHOULD respond with 202 (Accepted) response instead.
                             */
                        if (status === 201) {
                            parsedData = text;
                        } else {
                            // At present API tends to give back non-json response to instead of 204 on scenarios where there is not content
                            // Defensive code till API handles appropriately
                            parsedData = { response: text };
                        }

                        const err = LoggerHelper.createError(
                            `JSON parse failed: Body Content [${text}]`,
                            url,
                            error,
                            status
                        );

                        requestMethod !== 'PUT' &&
                            requestMethod !== 'POST' &&
                            logger.error({ err });
                    }
                    return {
                        status,
                        statusText,
                        data: parsedData,
                        headers: response.headers,
                    };
                });
            })
            .catch((error) => {
                if (
                    !__SERVER__ &&
                    get(
                        window,
                        '__PRELOADED_STATE__.context.featureFlags.enableNetworkFailureLogs',
                        false
                    )
                ) {
                    LoggerHelper.logErrorToBigData(url, requestMethod, error);
                }
                const err = LoggerHelper.createError('API Call failed', url, error);
                logger.error({ err });
                return {
                    status: 500,
                    statusText: `Error while fetching data from ${url}`,
                    error,
                };
            });
    }

    static invokeJsonp(url, options) {
        const logger = LoggerHelper.getLogger();
        return fetchJsonp(url, options)
            .then((response) => {
                const { status } = response;
                const { statusText } = response;
                return response.json().then((json) => ({ status, statusText, data: json }));
            })
            .catch((error) => {
                const err = LoggerHelper.createError('JSONP API Call failed', url, error);
                logger.error({ err });
                return {
                    status: 500,
                    statusText: `Error while fetching data from ${url}`,
                    error,
                };
            });
    }
}
